/**
 * useNotificationPolling Hook
 *
 * Provides real-time notification updates through polling.
 * Manages unread count, notification list, and all notification operations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationsApi } from '../services/api';

const POLLING_INTERVAL = 30000; // 30 seconds

/**
 * Hook for managing notifications with polling
 * @param {boolean} enabled - Whether polling should be active
 * @returns {Object} Notification state and methods
 */
export const useNotificationPolling = (enabled = true) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [unreadByCategory, setUnreadByCategory] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const intervalRef = useRef(null);
    const isInitialMount = useRef(true);

    /**
     * Fetch unread count (lightweight, for polling)
     */
    const refreshUnreadCount = useCallback(async () => {
        try {
            const response = await notificationsApi.getUnreadCount();
            if (response.ok && response.data) {
                setUnreadCount(response.data.unread_count || 0);
                setUnreadByCategory(response.data.by_category || {});
            }
        } catch (err) {
            console.error('Failed to fetch unread count:', err);
        }
    }, []);

    /**
     * Fetch notifications list
     */
    const fetchNotifications = useCallback(async (page = 1, category = null) => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: 20 };
            if (category) params.category = category;

            const response = await notificationsApi.getNotifications(params);
            if (response.ok && response.data) {
                setNotifications(response.data.notifications || []);
                setUnreadCount(response.data.unread_count || 0);
                setTotalCount(response.data.total || 0);
                setCurrentPage(response.data.page || 1);
            } else {
                setError('Failed to fetch notifications');
            }
        } catch (err) {
            setError('Failed to fetch notifications');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Mark single notification as read
     */
    const markAsRead = useCallback(async (notificationId) => {
        try {
            const response = await notificationsApi.markAsRead(notificationId);
            if (response.ok) {
                setNotifications(prev =>
                    prev.map(n =>
                        n.notification_id === notificationId
                            ? { ...n, is_read: true, read_at: new Date().toISOString() }
                            : n
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    }, []);

    /**
     * Mark all notifications as read (optionally by category)
     */
    const markAllAsRead = useCallback(async (category = null) => {
        try {
            const response = await notificationsApi.markAllAsRead(category);
            if (response.ok) {
                setNotifications(prev =>
                    prev.map(n =>
                        (!category || n.category === category)
                            ? { ...n, is_read: true, read_at: new Date().toISOString() }
                            : n
                    )
                );
                await refreshUnreadCount();
            }
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    }, [refreshUnreadCount]);

    /**
     * Delete a notification
     */
    const deleteNotification = useCallback(async (notificationId) => {
        try {
            const response = await notificationsApi.deleteNotification(notificationId);
            if (response.ok) {
                const deletedNotification = notifications.find(n => n.notification_id === notificationId);
                setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
                if (deletedNotification && !deletedNotification.is_read) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
                setTotalCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to delete notification:', err);
        }
    }, [notifications]);

    // Initial fetch and polling setup
    useEffect(() => {
        if (!enabled) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Initial fetch
        if (isInitialMount.current) {
            refreshUnreadCount();
            isInitialMount.current = false;
        }

        // Set up polling
        intervalRef.current = setInterval(() => {
            refreshUnreadCount();
        }, POLLING_INTERVAL);

        // Cleanup
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, refreshUnreadCount]);

    // Refresh when tab becomes visible
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && enabled) {
                refreshUnreadCount();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, refreshUnreadCount]);

    return {
        notifications,
        unreadCount,
        unreadByCategory,
        loading,
        error,
        totalCount,
        currentPage,
        fetchNotifications,
        refreshUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
    };
};

export default useNotificationPolling;
